import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IAuthUser } from '@app-core/interfaces/auth-user.interface';
import {
  ILoginRequest,
  ILoginResponse,
} from '@app-core/interfaces/login.interface';
import {
  IRegisterRequest,
  IRegisterResponse,
} from '@app-core/interfaces/register.interface';
import { IVerifyEmailResponse } from '@app-core/interfaces/verify.interface';
import { SessionRepository } from '@app-core/state/session.repository';
import { CountryCode } from '@app-shared/enums/country-codes.enum';
import { Observable, take, tap } from 'rxjs';
import { InterceptorSkipHeader } from 'src/app/defaults/interceptor.constants';
import { CountryRepository } from 'src/app/entities/countries/state/country.repository';
import { ActivityRepository } from 'src/app/user-usage/user-usage.repository';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly http = inject(HttpClient);
  private readonly sessionRepo = inject(SessionRepository);
  private readonly countryRepo = inject(CountryRepository);
  private readonly activityRepo = inject(ActivityRepository);

  /**
   * Get the logged-in user
   *
   * GET /me
   */
  getLoggedInUser(): Observable<IAuthUser> {
    return this.http
      .get<IAuthUser>(environment.apiUrl + `/me`, {
        headers: InterceptorSkipHeader,
      })
      .pipe(
        tap(res => {
          if (res.region) {
            this.countryRepo.setCountryByCode(CountryCode.REGION);
            this.sessionRepo.setStoreProp('savedRoute', null);
          }
        }),
      );
  }

  /**
   * Login user.
   *
   * POST /login
   *
   * @param data
   */
  login(data: ILoginRequest): Observable<ILoginResponse> {
    return this.http
      .post<ILoginResponse>(environment.apiUrl + `/login`, data)
      .pipe(
        tap(res => {
          if (res.user.region) {
            this.countryRepo.setCountryByCode(CountryCode.REGION);
            this.sessionRepo.setStoreProp('savedRoute', null);
          }

          this.sessionRepo.saveLoginInfo(res);
        }),
      );
  }

  /**
   * Register user
   *
   * POST /register
   *
   * @param data
   * @param inviteToken Invite token for the user that was invited to register on the app
   */
  register(
    data: IRegisterRequest,
    inviteToken?: string | null,
  ): Observable<IRegisterResponse> {
    const queryParams: { [param: string]: string } = {};

    if (inviteToken) queryParams['invite'] = inviteToken;

    return this.http.post<IRegisterResponse>(
      environment.apiUrl + `/register`,
      data,
      {
        params: queryParams,
      },
    );
  }

  /**
   * Verifies user's token and activates the account.
   *
   * POST /email/verify
   *
   * @param activationToken
   */
  verifyEmail(activationToken: string): Observable<IVerifyEmailResponse> {
    return this.http.post<IVerifyEmailResponse>(
      environment.apiUrl + `/email/verify`,
      { activationToken },
    );
  }

  /**
   * Changes the user's password
   *
   * PATCH /reset-password/{resetToken}
   *
   * @param token
   * @param password
   */
  resetPassword(token: string, password: string): Observable<void> {
    return this.http.patch<void>(
      environment.apiUrl + `/reset-password/${token}`,
      {
        password,
      },
    );
  }

  /**
   * Changes the user's password (expired password scenario)
   *
   * PATCH /change-password/{resetToken}
   *
   * @param token
   * @param password
   */
  changePassword(
    token: string,
    oldPassword: string,
    newPassword: string,
    newPasswordRepeat: string,
  ): Observable<void> {
    return this.http.patch<void>(
      environment.apiUrl + `/change-password/${token}`,
      {
        'old-password': oldPassword,
        'new-password': newPassword,
        'new-password-repeat': newPasswordRepeat,
      },
    );
  }

  /**
   * Sends the user an email with a link to reset his password.
   *
   * POST /reset-password
   *
   * @param token
   */
  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(environment.apiUrl + `/reset-password`, {
      email,
    });
  }

  /**
   * Validate reset password token to check if it was already used.
   *
   * GET /validate-reset-password/{hash}
   *
   * @param token
   */
  validateResetPasswordToken(token: string): Observable<void> {
    return this.http.get<void>(
      environment.apiUrl + `/validate-reset-password/${token}`,
    );
  }

  /**
   * Logs out a user and invalidates his token by calling API
   */
  logout(): void {
    const { token_key, refresh_token, authUser } =
      this.sessionRepo.getStoreValue();

    if (!token_key || !refresh_token || !authUser) {
      this.activityRepo.stopTracking();
      this.sessionRepo.resetStore();
    } else {
      this.activityRepo.stopTracking();
      this.http
        .delete(environment.apiUrl + `/logout`)
        .pipe(take(1))
        .subscribe(() => {
          this.countryRepo.setActiveCountryByID(authUser.country.id);
        });
      this.sessionRepo.resetStore();
    }
  }

  /**
   * Gets the URL for the RX authentication page to send the user there to login
   *
   * GET /rx-authentication-url
   */
  getSwissRxAuthURL(): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(
      environment.apiUrl + `/rx-authentication-url`,
    );
  }

  /**
   * Gets the URL for the My Sandoz authentication page to send the user there to login
   *
   * GET /rx-authentication-url
   */
  getMySandozAuthURL(): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(
      environment.apiUrl + `/my-sandoz-authentication-url`,
    );
  }

  /**
   * Sends the RX generated code to the backend to create a login session.
   *
   * POST /rx-authorization-code
   *
   * @param code - token code that Swiss RX generates
   */
  authenticateWithSwissRX(code: string): Observable<ILoginResponse> {
    return this.http
      .post<ILoginResponse>(environment.apiUrl + `/rx-authorization-code`, {
        code,
      })
      .pipe(
        tap(res => {
          if (res.user.region) {
            this.countryRepo.setCountryByCode(CountryCode.REGION);
            this.sessionRepo.setStoreProp('savedRoute', null);
          }

          this.sessionRepo.saveLoginInfo(res);
        }),
      );
  }

  /**
   * Sends the My Sandoz generated code to the backend to create a login session.
   *
   * POST /mysandoz/validate
   *
   * @param code - token code that My Sandoz generates
   */
  authenticateWithMySandoz(token: string): Observable<ILoginResponse> {
    return this.http
      .get<ILoginResponse>(environment.apiUrl + `/mysandoz/validate`, {
        params: {
          token,
        },
      })
      .pipe(
        tap(res => {
          if (res.user.region) {
            this.countryRepo.setCountryByCode(CountryCode.REGION);
            this.sessionRepo.setStoreProp('savedRoute', null);
          }

          this.sessionRepo.saveLoginInfo(res);
        }),
      );
  }

  /**
   * Send the conectafarm token to the backend to create a login session.
   *
   * POST /conectafarm-authorization-code
   * @param token
   */
  authenticateWithConectafarm(token: string): Observable<ILoginResponse> {
    return this.http
      .post<ILoginResponse>(
        environment.apiUrl + `/conectafarm-authorization-code`,
        { token },
      )
      .pipe(
        tap(res => {
          if (res.user.region) {
            this.countryRepo.setCountryByCode(CountryCode.REGION);
            this.sessionRepo.setStoreProp('savedRoute', null);
          }

          this.sessionRepo.saveLoginInfo(res);
        }),
      );
  }
}
